import React, { useState } from "react";
import { Heart, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ImpactCounter } from "../components/ImpactCounter";

const PRESETS = [10, 25, 50, 100];

export default function Donate() {
    const [preset, setPreset] = useState(25);
    const [custom, setCustom] = useState("");
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);

    const amount = custom !== "" ? parseFloat(custom) : preset;
    const valid = Number.isFinite(amount) && amount >= 1 && amount <= 25000;

    const submit = async (e) => {
        e.preventDefault();
        if (!valid) return toast.error("Please enter an amount between $1 and $25,000.");
        setLoading(true);
        try {
            const r = await api.post("/donate/session", {
                amount,
                origin_url: window.location.origin,
                donor_email: email || null,
                donor_name: name || null,
            });
            window.location.href = r.data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again.");
            setLoading(false);
        }
    };

    return (
        <div data-testid="donate-page">
            <section className="px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-16 md:pb-24 grid md:grid-cols-12 gap-12 lg:gap-20">
                <div className="md:col-span-6 fade-up">
                    <div className="eyebrow text-[#B8860B] mb-6 flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-[#D4AF37]" />
                        Support the Mission
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] mb-8">
                        Give the gift of <em className="italic text-[#B8860B]">kindness</em>.
                    </h1>
                    <p className="text-base md:text-lg text-black/65 font-light leading-relaxed max-w-lg mb-10">
                        Every dollar keeps the microphones on and fuels our programs — Pieces of Hope,
                        Purpose in the Process, Haven of Hope, and the GOK Second Chance Program.
                        Your generosity reaches real people in real seasons of need.
                    </p>
                    <div className="border-l-2 border-[#D4AF37] pl-6 max-w-lg">
                        <p className="text-sm text-black/70 leading-relaxed [font-variant-ligatures:none]">
                            The Gift of Kindness Podcast is a registered 501(c)(3) nonprofit organization.
                            Donations are tax-deductible to the fullest extent allowed by law.
                        </p>
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    data-testid="donate-form"
                    className="md:col-span-6 lg:col-span-5 lg:col-start-8 bg-[#0A0A0A] text-white p-8 md:p-10 fade-up"
                >
                    <div className="flex items-center gap-3 mb-8">
                        <Heart size={18} className="text-[#D4AF37]" strokeWidth={1.5} />
                        <span className="eyebrow text-[#D4AF37] text-[11px]">One-Time Donation</span>
                    </div>

                    <div className="grid grid-cols-4 gap-3 mb-4">
                        {PRESETS.map((p) => {
                            const active = custom === "" && preset === p;
                            return (
                                <button
                                    key={p}
                                    type="button"
                                    data-testid={`donate-preset-${p}`}
                                    onClick={() => { setPreset(p); setCustom(""); }}
                                    className={`py-4 font-serif text-xl border transition-colors duration-300 ${
                                        active
                                            ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                                            : "border-white/20 text-white hover:border-[#D4AF37] hover:text-[#D4AF37]"
                                    }`}
                                >
                                    ${p}
                                </button>
                            );
                        })}
                    </div>

                    <label className="block mb-8">
                        <span className="eyebrow text-[10px] text-white/50 block mb-2">Or enter a custom amount</span>
                        <div className="flex items-center border border-white/20 focus-within:border-[#D4AF37] transition-colors">
                            <span className="pl-4 font-serif text-2xl text-white/60">$</span>
                            <input
                                data-testid="donate-custom-amount"
                                type="number"
                                min="1"
                                max="25000"
                                step="1"
                                inputMode="decimal"
                                placeholder="Other"
                                value={custom}
                                onChange={(e) => setCustom(e.target.value)}
                                className="w-full bg-transparent px-3 py-4 font-serif text-2xl outline-none placeholder:text-white/30"
                            />
                        </div>
                    </label>

                    <div className="grid sm:grid-cols-2 gap-4 mb-8">
                        <input
                            data-testid="donate-name"
                            type="text"
                            placeholder="Your name (optional)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-sm font-light placeholder:text-white/40 transition-colors"
                        />
                        <input
                            data-testid="donate-email"
                            type="email"
                            placeholder="Email for receipt (optional)"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-sm font-light placeholder:text-white/40 transition-colors"
                        />
                    </div>

                    <button
                        type="submit"
                        data-testid="donate-submit"
                        disabled={loading || !valid}
                        className="w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-8 py-4 eyebrow hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                        {valid ? `Donate $${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "Enter an amount"}
                        {!loading && <ArrowRight size={16} />}
                    </button>
                    <p className="text-[11px] text-white/40 mt-4 text-center font-light">
                        Secure payment powered by Stripe. You'll be redirected to complete your gift.
                    </p>
                </form>
            </section>

            <ImpactCounter />

            <section className="border-t border-black/10 px-6 md:px-12 lg:px-20 py-16 md:py-20 grid md:grid-cols-3 gap-10">
                {[
                    ["Programs", "Direct support for autism awareness, shelter, re-entry, and purpose-finding initiatives."],
                    ["Production", "Studio time, editing, and hosting so every story reaches the listeners who need it."],
                    ["Community", "Meetups, live rooms, and resources that turn listeners into neighbors."],
                ].map(([title, body]) => (
                    <div key={title}>
                        <div className="font-serif text-2xl mb-3">{title}</div>
                        <p className="text-black/60 font-light leading-relaxed text-sm">{body}</p>
                    </div>
                ))}
            </section>
        </div>
    );
}
